import { useRef, useState } from "react";
import { Camera, ImagePlus, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, Field, Loading, Plate, useFetch } from "@/components/common";
import { SecureImg, uploadPhoto } from "@/components/Photos";
import { WarningsBox, warningsOf, PleinStatus, ReceiptReplace } from "@/components/Pleins";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errMsg, fDate, fEur, fNum } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const FUELS = ["SP95-E10", "SP95", "SP98", "Gazole", "E85", "GPL"];
export { FUELS };
const pad = (n) => String(n).padStart(2, "0");
export const nowParts = () => {
  const d = new Date();
  return { date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`, time: `${pad(d.getHours())}:${pad(d.getMinutes())}` };
};
const n = (s) => (s === "" || s == null ? null : Number(String(s).replace(",", ".")));

export function ReceiptPicker({ fileId, setFileId }) {
  const cam = useRef();
  const gal = useRef();
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      setFileId(await uploadPhoto(file, "receipt"));
    } catch (er) {
      toast.error(errMsg(er));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div data-testid="receipt-picker">
      <input ref={cam} type="file" accept="image/*" capture="environment" className="hidden" onChange={pick} data-testid="receipt-camera-input" />
      <input ref={gal} type="file" accept="image/*" className="hidden" onChange={pick} data-testid="receipt-gallery-input" />
      {fileId ? (
        <div className="space-y-2">
          <SecureImg id={fileId} className="w-full max-h-72 object-contain rounded-lg border bg-slate-50" testid="receipt-preview" />
          <Button type="button" variant="outline" className="w-full" onClick={() => cam.current.click()} data-testid="receipt-replace"><RefreshCw className="w-4 h-4 mr-1" /> Photo illisible ? Reprendre</Button>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-2">
          <button type="button" onClick={() => cam.current.click()} className="h-14 rounded-lg bg-[#0E2D43] text-white font-semibold text-sm flex items-center justify-center gap-2" data-testid="receipt-camera">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />} Photo du ticket
          </button>
          <button type="button" onClick={() => gal.current.click()} className="h-14 rounded-lg border-2 border-[#0E2D43] text-[#0E2D43] font-semibold text-sm flex items-center justify-center gap-2" data-testid="receipt-gallery">
            <ImagePlus className="w-4 h-4" /> Galerie
          </button>
        </div>
      )}
    </div>
  );
}

function MyPleins({ k }) {
  const { data, reload } = useFetch("/pleins", { k });
  const rows = data || [];
  if (!rows.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="my-pleins">
      <div className="font-display font-bold text-[#0E2D43] mb-2">Mes derniers pleins</div>
      <div className="divide-y">{rows.slice(0, 10).map((t) => (
        <div key={t.id} className="py-2 flex flex-wrap items-center gap-2 text-sm" data-testid={`my-plein-${t.id}`}>
          <span className="font-semibold">{fDate(t.date)}</span><span>{fNum(t.liters, 2)} L</span><span className="font-semibold">{fEur(t.amount_ttc)}</span>
          <span className="text-xs text-slate-500">{t.mileage ? `${fNum(t.mileage)} km` : ""}</span><PleinStatus v={t.control_status} />
          <span className="ml-auto"><ReceiptReplace t={t} onDone={reload} /></span>
        </div>
      ))}</div>
    </div>
  );
}

export default function Plein() {
  const { user } = useAuth();
  const ctx = useFetch("/pleins/context");
  const [f, setF] = useState({ ...nowParts(), station: "", fuel: "", mileage: "", liters: "", amount_ttc: "", amount_ht: "", vat: "", comment: "" });
  const [fileId, setFileId] = useState(null);
  const [warn, setWarn] = useState(null);
  const [busy, setBusy] = useState(false);
  const [k, setK] = useState(0);
  if (ctx.loading) return <Loading />;
  const v = ctx.data?.vehicle;
  const set = (key) => (e) => { setF({ ...f, [key]: e.target ? e.target.value : e }); setWarn(null); };
  const ttc = n(f.amount_ttc), liters = n(f.liters), ht = n(f.amount_ht), vat = n(f.vat);
  const ppl = ttc && liters ? ttc / liters : null;
  const mismatch = ht != null && vat != null && ttc != null && Math.abs(ht + vat - ttc) > 0.05;
  const ready = v && f.date && f.time && f.station.trim() && f.fuel && n(f.mileage) > 0 && liters > 0 && ttc > 0 && fileId;
  const send = async (confirm) => {
    setBusy(true);
    try {
      const { data } = await api.post("/pleins", { ...f, mileage: Math.round(n(f.mileage)), liters, amount_ttc: ttc, amount_ht: ht, vat, file_id: fileId, confirm });
      toast.success(data.merged ? "Plein enregistré et rapproché de la transaction Fleet Pro" : "Plein enregistré");
      setF({ ...nowParts(), station: "", fuel: f.fuel, mileage: "", liters: "", amount_ttc: "", amount_ht: "", vat: "", comment: "" });
      setFileId(null);
      setWarn(null);
      setK((x) => x + 1);
      ctx.reload();
    } catch (e) {
      const w = warningsOf(e);
      if (w) setWarn(w);
      else toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="max-w-xl space-y-4" data-testid="plein-page">
      <PageHeader overline="Véhicules commerciaux · Fleet Pro" title="Enregistrer un plein" />
      <div className="bg-white border border-slate-200 rounded-xl p-4 sm:p-5 space-y-3 rise">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-semibold text-[#0E2D43]" data-testid="plein-user">{user.name}</span>
          {v ? <Plate>{v.plate}</Plate> : <span className="text-sm text-rose-600" data-testid="plein-no-vehicle">Aucun véhicule affecté</span>}
          <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-[#E8F1FB] text-[#1d5fa8]" data-testid="plein-provider">Fleet Pro</span>
        </div>
        {v?.mileage && <div className="text-xs text-slate-500" data-testid="plein-last-km">Dernier kilométrage connu : {fNum(v.mileage)} km{v.mileage_date ? ` le ${fDate(v.mileage_date)}` : ""}{v.mileage_source_label ? ` (${v.mileage_source_label})` : ""}</div>}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Date *"><Input type="date" value={f.date} onChange={set("date")} className="h-11" data-testid="plein-date" /></Field>
          <Field label="Heure *"><Input type="time" value={f.time} onChange={set("time")} className="h-11" data-testid="plein-time" /></Field>
        </div>
        <Field label="Station-service *"><Input value={f.station} onChange={set("station")} placeholder="ex. TotalEnergies Aubagne" className="h-11" data-testid="plein-station" /></Field>
        <Field label="Carburant *">
          <div className="flex flex-wrap gap-2" data-testid="plein-fuel">{FUELS.map((x) => (
            <button key={x} type="button" onClick={() => set("fuel")(x)} data-testid={`plein-fuel-${x}`} className={`px-3 h-10 rounded-lg border text-sm font-semibold transition-colors ${f.fuel === x ? "bg-[#0E2D43] text-white border-[#0E2D43]" : "bg-white text-slate-600 border-slate-200"}`}>{x}</button>
          ))}</div>
        </Field>
        <Field label="Kilométrage compteur *"><Input inputMode="numeric" value={f.mileage} onChange={set("mileage")} className="h-11 text-lg font-semibold" data-testid="plein-mileage" /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Litres *"><Input inputMode="decimal" value={f.liters} onChange={set("liters")} className="h-11" data-testid="plein-liters" /></Field>
          <Field label="Montant TTC (€) *"><Input inputMode="decimal" value={f.amount_ttc} onChange={set("amount_ttc")} className="h-11" data-testid="plein-ttc" /></Field>
          <Field label="Montant HT (€)"><Input inputMode="decimal" value={f.amount_ht} onChange={set("amount_ht")} className="h-11" data-testid="plein-ht" /></Field>
          <Field label="TVA (€)"><Input inputMode="decimal" value={f.vat} onChange={set("vat")} className="h-11" data-testid="plein-vat" /></Field>
        </div>
        <div className="text-sm" data-testid="plein-ppl">Prix au litre : <b className="text-[#0E2D43]">{ppl ? `${fNum(ppl, 3)} €/L` : "—"}</b></div>
        {mismatch && <div className="text-xs text-rose-700" data-testid="plein-amount-mismatch">Incohérence : HT + TVA = {fNum(ht + vat, 2)} € ≠ TTC {fNum(ttc, 2)} €</div>}
        <Field label="Photo du justificatif / ticket *"><ReceiptPicker fileId={fileId} setFileId={setFileId} /></Field>
        <Field label="Commentaire"><Input value={f.comment} onChange={set("comment")} className="h-11" data-testid="plein-comment" /></Field>
        <WarningsBox warnings={warn} testid="plein-warnings" />
        {warn ? (
          <div className="grid grid-cols-2 gap-2">
            <Button variant="outline" className="h-12" onClick={() => setWarn(null)} data-testid="plein-fix">Corriger</Button>
            <Button className="h-12 bg-amber-600 hover:bg-amber-700" disabled={busy} onClick={() => send(true)} data-testid="plein-confirm-anomaly">Confirmer quand même</Button>
          </div>
        ) : (
          <Button className="w-full h-12 bg-[#F49619] hover:bg-[#E0830B] text-white font-semibold" disabled={!ready || busy} onClick={() => send(false)} data-testid="plein-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enregistrer le plein"}
          </Button>
        )}
        {!fileId && <p className="text-xs text-slate-500">La photo du ticket est obligatoire.</p>}
      </div>
      <MyPleins k={k} />
    </div>
  );
}
